import { CustomInputScopeSchema } from "@/types/general";
import z from "zod";

export const AssignedUserSchema = z.object({
  userId: z.any().optional(),
});

export const NoteSchema = z.object({
  note: z.string().min(1, "Note is required"),
  createdById: z.string().min(1, "CreatedBy is required"),
  createdAt: z.string(),
  createdByName: z.string().optional(),
  /** "Switch user": the REAL author when a manager wrote this while viewing
   *  as `createdById`. Absent on every ordinary note. Display only — the
   *  edit/delete gate still compares `createdById` to the signed-in user. */
  switchedByName: z.string().nullable().optional(),
  // DB id — named noteId because useFieldArray reserves `id` for its own key.
  // Absent on just-typed notes until the lead/child payload is refetched.
  noteId: z.number().optional(),
  // WhatsApp-style soft delete / edited marker.
  isDeleted: z.boolean().optional(),
  editedAt: z.string().nullable().optional(),
});

export const FollowUpSchema = z.object({
  date: z.string().min(1, "Follow-up date is required"),
  id: z.string().or(z.number()),
  // ✅ FIXED: Use the schema directly, do not wrap in z.object()
  note: z.any().optional(),
  createdBy: z.any(),
  createdAt: z.string(),
  status: z.enum(["Pending", "Complete"]),
  completedBy: z.object({}).or(z.any()).optional(),
  isSystem: z.boolean(),
});

/** Same shape as follow-ups (date, status, note, …). */
export const DeadlineSchema = FollowUpSchema;

export const LeadSchema = z.object({
  name: z.string().optional(),
  phoneCountryCode: z
    .string()
    .regex(/^\+\d{1,4}$/, "Country code required"),
  phone: z
    .string()
    .min(6, "Valid mobile number is required")
    .regex(/^\d+$/, "Digits only"),
  whatsapp: z.string().optional(),
  /** Form-only companion of `whatsapp`: the picker's code. The context joins
   *  it back into ONE `whatsapp` string ("+<cc> <national>") before the
   *  request — the server has no such column (formatLeadWhatsapp.ts). */
  whatsappCountryCode: z
    .string()
    .regex(/^\+\d{1,4}$/, "Country code required")
    .optional(),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  gender: z.enum(["Male", "Female", "Other"]).optional(),
  location: z.string().optional(),

  leadSourceId: z.string().optional(),
  campaignId: z.string().optional(),
  branchId: z.string().optional(),
  userId: z.string().optional(),

  followUpDate: z.string().optional(),
  /** ISO timestamp. Only rendered/sent when Settings → Manage → "Add/Edit
   *  Created At" is on; absent = the server stamps now(). */
  createdAt: z.string().optional(),
  customInputScopes: z.array(CustomInputScopeSchema).optional(),
  notes: z.array(NoteSchema),
  followUps: z.array(FollowUpSchema),
  deadlines: z.array(DeadlineSchema),
  documents: z.array(z.any()),
});

export const leadDefaultValues: z.infer<typeof LeadSchema> = {
  name: "",
  phoneCountryCode: "+91",
  phone: "",
  whatsapp: "",
  whatsappCountryCode: "+91",
  email: "",
  gender: "Male",
  leadSourceId: "",
  campaignId: "",
  branchId: "",
  userId: "",
  customInputScopes: [],
  notes: [],
  followUps: [],
  deadlines: [],
  followUpDate: new Date().toISOString(),
  location: "",
  documents: [],
};
